import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";

const styles = {
  page: {
    background: "#000",
    color: "#fff",
    minHeight: "100vh",
    padding: "64px 24px",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', sans-serif",
  },

  container: {
    maxWidth: "680px",
    margin: "0 auto",
  },

  // ── Hero ──────────────────────────────────────────────────────────────────
  hero: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    marginBottom: "48px",
    paddingBottom: "40px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },

  avatar: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "1.5px solid rgba(168,85,247,0.4)",
  },

  heroText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  name: {
    fontSize: "28px",
    fontWeight: "700",
    letterSpacing: "-0.03em",
    margin: 0,
    color: "#fff",
  },

  verifiedIcon: {
    color: "#a855f7",
    fontSize: "18px",
    marginTop: "2px",
  },

  title: {
    fontSize: "14px",
    fontWeight: "500",
    color: "rgba(255,255,255,0.5)",
    margin: 0,
    letterSpacing: "-0.01em",
  },

  subtitle: {
    fontSize: "13px",
    fontWeight: "400",
    color: "rgba(255,255,255,0.35)",
    margin: 0,
    letterSpacing: "-0.01em",
  },

  // ── Badge ─────────────────────────────────────────────────────────────────
  badge: {
    display: "inline-block",
    background: "rgba(168,85,247,0.15)",
    border: "1px solid rgba(168,85,247,0.4)",
    color: "#c084fc",
    fontSize: "11px",
    fontWeight: "600",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: "6px 14px",
    borderRadius: "100px",
    marginBottom: "20px",
  },

  // ── Section ───────────────────────────────────────────────────────────────
  section: {
    marginBottom: "48px",
    paddingBottom: "40px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },

  sectionLabel: {
    fontSize: "11px",
    fontWeight: "600",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.35)",
    marginBottom: "16px",
  },

  sectionTitle: {
    fontSize: "22px",
    fontWeight: "700",
    letterSpacing: "-0.03em",
    color: "#fff",
    marginBottom: "16px",
  },

  bodyText: {
    fontSize: "15px",
    lineHeight: "1.7",
    color: "rgba(255,255,255,0.6)",
    letterSpacing: "-0.01em",
  },

  divider: {
    border: "none",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    margin: "0 0 48px 0",
  },

  // ── Find Me ───────────────────────────────────────────────────────────────
  findMeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    marginTop: "20px",
  },

  findMeCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "20px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    textAlign: "center",
  },

  findMeLogo: {
    width: "80px",
    height: "auto",
  },

  findMeDesc: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.45)",
    lineHeight: "1.5",
    letterSpacing: "-0.01em",
  },

  findMeBtn: (bg) => ({
    display: "inline-block",
    padding: "9px 16px",
    backgroundColor: bg,
    color: "#fff",
    borderRadius: "8px",
    textDecoration: "none",
    fontWeight: "600",
    fontSize: "13px",
    letterSpacing: "-0.01em",
    marginTop: "auto",
    transition: "opacity 0.2s",
  }),

  // ── Stats ─────────────────────────────────────────────────────────────────
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    marginTop: "20px",
  },

  statCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "24px 16px",
    textAlign: "center",
  },

  statNumber: {
    fontSize: "28px",
    fontWeight: "700",
    letterSpacing: "-0.04em",
    color: "#fff",
  },

  statLabel: {
    fontSize: "12px",
    color: "rgba(255,255,255,0.4)",
    marginTop: "6px",
    letterSpacing: "-0.01em",
  },

  // ── Skills ────────────────────────────────────────────────────────────────
  skillsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "16px",
  },

  skill: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.7)",
    padding: "7px 13px",
    borderRadius: "8px",
    fontSize: "13px",
    letterSpacing: "-0.01em",
  },

  // ── Experience ────────────────────────────────────────────────────────────
  expCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "14px",
    padding: "20px",
    marginBottom: "10px",
  },

  expRole: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#fff",
    letterSpacing: "-0.02em",
    margin: "0 0 4px",
  },

  expDate: {
    fontSize: "12px",
    color: "#a855f7",
    marginBottom: "8px",
  },

  expDesc: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.5)",
    lineHeight: "1.6",
    letterSpacing: "-0.01em",
    margin: 0,
  },

  // ── Video ─────────────────────────────────────────────────────────────────
  videoWrap: {
    marginTop: "20px",
    borderRadius: "16px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
  },

  video: {
    width: "100%",
    display: "block",
  },

  videoCaption: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.3)",
    textAlign: "center",
    marginTop: "12px",
    letterSpacing: "-0.01em",
    fontStyle: "italic",
  },

  // ── PDF ───────────────────────────────────────────────────────────────────
  pdfDesc: {
    fontSize: "15px",
    color: "rgba(255,255,255,0.5)",
    lineHeight: "1.6",
    letterSpacing: "-0.01em",
    marginBottom: "20px",
  },

  pdfBtn: {
    padding: "13px 26px",
    background: "#a855f7",
    border: "none",
    borderRadius: "12px",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    letterSpacing: "-0.01em",
    transition: "opacity 0.2s",
  },

  pdfOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.85)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    backdropFilter: "blur(6px)",
  },

  pdfModal: {
    position: "relative",
    width: "85%",
    height: "85%",
    backgroundColor: "#111",
    borderRadius: "16px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.12)",
  },

  pdfIframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },

  closeBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    backgroundColor: "#a855f7",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    fontSize: "16px",
    cursor: "pointer",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // ── Audio ─────────────────────────────────────────────────────────────────
  audioCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "12px",
  },

  audioLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#fff",
    letterSpacing: "-0.02em",
    marginBottom: "14px",
  },

  audioPlayer: {
    width: "100%",
    outline: "none",
    accentColor: "#a855f7",
  },

  audioNote: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.4)",
    marginTop: "14px",
    lineHeight: "1.6",
    fontStyle: "italic",
    letterSpacing: "-0.01em",
  },
};

const YK = () => {
  const [showPdf, setShowPdf] = useState(false);

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Hero */}
        <div style={styles.hero}>
          <img
            src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/biobackground.png"
            alt="Yawar Khan"
            style={styles.avatar}
          />
          <div style={styles.heroText}>
            <div style={styles.nameRow}>
              <h1 style={styles.name}>Yawar Khan</h1>
              <FaCheckCircle style={styles.verifiedIcon} />
            </div>
            <p style={styles.title}>VP of Sales & Marketing · Software Developer</p>
            <p style={styles.subtitle}>Co-founder & CEO — Reflex Advisor</p>
          </div>
        </div>

        {/* About */}
        <section style={styles.section}>
          <div style={styles.badge}>About</div>
          <h2 style={styles.sectionTitle}>B2B Sales Growth Expert & Sales Closer</h2>
          <p style={styles.bodyText}>
            I handle the full sales and marketing side so founders don't have to.
            I book meetings, close deals, run marketing, build and manage websites
            (front and back end), design graphics, retain clients, support
            customers, and train sales teams. With 15+ years experience, strong
            Upwork and LinkedIn results, and work at Reflex Advisor, I help
            companies grow faster and close more business.
          </p>
        </section>

        {/* Find Me On */}
        <section style={styles.section}>
          <div style={styles.badge}>Find Me On</div>
          <div style={styles.findMeGrid}>
            <div style={styles.findMeCard}>
              <img src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/upwork.png" alt="Upwork" style={styles.findMeLogo} />
              <p style={styles.findMeDesc}>Proven track record and client reviews.</p>
              <a
                href="https://www.upwork.com/freelancers/~01b17fa0ef8a60dfac"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#6fda44")}
              >
                Upwork Profile
              </a>
            </div>
            <div style={styles.findMeCard}>
              <img src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/linkedin.png" alt="LinkedIn" style={styles.findMeLogo} />
              <p style={styles.findMeDesc}>Career journey and recommendations.</p>
              <a
                href="https://www.linkedin.com/in/yavar-khan1/"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#0077b5")}
              >
                LinkedIn
              </a>
            </div>
            <div style={styles.findMeCard}>
              <img src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/podcast.png" alt="Podcast" style={styles.findMeLogo} />
              <p style={styles.findMeDesc}>Sales, marketing & growth strategy episodes.</p>
              <a
                href="https://www.podcast.com"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#a855f7")}
              >
                Podcast
              </a>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section style={styles.section}>
          <div style={styles.badge}>15 Years of Results</div>
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>50+</div>
              <div style={styles.statLabel}>Total Jobs</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>10K+</div>
              <div style={styles.statLabel}>Meetings Booked</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>$1M+</div>
              <div style={styles.statLabel}>Revenue Generated</div>
            </div>
          </div>
        </section>

        {/* Skills */}
        <section style={styles.section}>
          <div style={styles.badge}>Skills</div>
          <div style={styles.skillsWrap}>
            {[
              "Lead Generation", "Sales Closing", "Appointment Setting",
              "B2B Marketing Strategy", "Digital Marketing", "SEO & SEM",
              "Google & Facebook Ads", "Website Development", "Software Development",
              "Graphic Design", "Client Retention", "Customer Support",
              "Sales Team Training", "Business Scaling", "CRM Management",
            ].map(s => (
              <span key={s} style={styles.skill}>{s}</span>
            ))}
          </div>
        </section>

        {/* Experience */}
        <section style={styles.section}>
          <div style={styles.badge}>Experience</div>
          {[
            { role: "CEO — Reflex Advisor", date: "2024 – Present", desc: "Leading business operations, client strategy, and growth initiatives for international clients." },
            { role: "Business Development Officer — Upwork", date: "2021 – Present", desc: "Managed sales pipelines, closed high-value deals, and built strong partnerships with stakeholders." },
            { role: "Sales Consultant — Accenture", date: "2021 – 2022", desc: "Worked on sales transformation and go-to-market strategies for global clients, improving revenue performance." },
            { role: "Sales Closer — Oxo", date: "2022 – 2023", desc: "Drove SaaS adoption, managed key accounts, and helped clients leverage knowledge-sharing tools for growth." },
            { role: "Sales Closer — Qisstpay", date: "2020 – 2021", desc: "Closed deals and built long-term client relationships." },
          ].map(exp => (
            <div key={exp.role} style={styles.expCard}>
              <p style={styles.expRole}>{exp.role}</p>
              <p style={styles.expDate}>{exp.date}</p>
              <p style={styles.expDesc}>{exp.desc}</p>
            </div>
          ))}
        </section>

        {/* Video */}
        <section style={styles.section}>
          <div style={styles.badge}>Presentation</div>
          <div style={styles.videoWrap}>
            <video
              style={styles.video}
              controls
              poster="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/reflexthumbnail.png"
            >
              <source src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/videos/yawarvideo.mp4" type="video/mp4" />
            </video>
          </div>
          <p style={styles.videoCaption}>
            Made for Upwork clients to showcase my presentation and selling approach.
          </p>
        </section>

        {/* PDF */}
        <section style={styles.section}>
          <div style={styles.badge}>Past Work</div>
          <h2 style={styles.sectionTitle}>My Past Work Projects</h2>
          <p style={styles.pdfDesc}>
            Projects across LinkedIn, Upwork, and Reflex Advisor — working alongside founders and CEOs. 
            The goal was simple: move fast, see every angle of business, and learn what actually scales. 
            These are the ones I went deepest on.
          </p>
          <button
            style={styles.pdfBtn}
            onClick={() => setShowPdf(true)}
            onMouseOver={e => e.currentTarget.style.opacity = "0.8"}
            onMouseOut={e => e.currentTarget.style.opacity = "1"}
          >
            View PDF
          </button>
        </section>

        {showPdf && (
          <div style={styles.pdfOverlay}>
            <div style={styles.pdfModal}>
              <button style={styles.closeBtn} onClick={() => setShowPdf(false)}>✕</button>
              <iframe
                src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/docs/yawarwork.pdf"
                title="Past Work PDF"
                style={styles.pdfIframe}
              />
            </div>
          </div>
        )}

        {/* Call Recordings */}
        <section>
          <div style={styles.badge}>Call Recordings</div>
          <div style={styles.audioCard}>
            <div style={styles.audioLabel}>Reflex Advisor – Discovery Call by Yawar Khan</div>
            <audio controls style={styles.audioPlayer}>
              <source src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/audios/reflexrecording.mp3" type="audio/mpeg" />
            </audio>
            <p style={styles.audioNote}>
              On my first call with a cleaning company owner, I offered $50 to get started. 
              Fast forward — I upsold to $1,000, and in 3 months we scaled his business, 
              delivering premium results and boosting revenue.
            </p>
          </div>

          <div style={styles.audioCard}>
            <div style={styles.audioLabel}>Discovery Call with Roofing Company Owner</div>
            <audio controls style={styles.audioPlayer}>
              <source src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/audios/meg.mp3" type="audio/mpeg" />
            </audio>
            <p style={styles.audioNote}>
              Call with Meg, a roofing company owner, where she shared all the details 
              and we discussed everything thoroughly.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default YK;